import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { getPublicSlotUsage } from "@/lib/publicBooking";
import {
  applyConflictResolution,
  computeOccurrenceDates,
  describeStatus,
  evaluateOccurrence,
  PlannedOccurrence,
  ScheduleData,
} from "@/lib/recurringSchedule";

const WEEKDAY_NAME = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

interface Props {
  listingId: string;
  totalSpots: number;
  pricePerPax: number;
  pax: number;
  selectedDate: Date | undefined;
  selectedTime: string | undefined;
  schedule: ScheduleData | null;
  enabled: boolean;
  paymentMethod?: "stripe" | "manual";
  /** Per-listing billing mode. 'upfront' = pay the whole programme today;
   *  'per_session' = pay week 1 now and each later week 24h before its slot. */
  billingMode?: "upfront" | "per_session";
  onEnabledChange: (v: boolean) => void;
  onPlanChange: (occurrences: PlannedOccurrence[]) => void;
}

const MIN_WEEKS = 2;
const MAX_WEEKS = 12;

export default function RecurringBookingPanel({
  listingId,
  totalSpots,
  pricePerPax,
  pax,
  selectedDate,
  selectedTime,
  schedule,
  enabled,
  paymentMethod,
  billingMode,
  onEnabledChange,
  onPlanChange,
}: Props) {
  const [weeks, setWeeks] = useState<number>(4);
  const [resolution, setResolution] = useState<"skip" | "trim">("skip");
  const [capacityByDate, setCapacityByDate] = useState<Record<string, number>>({});
  const [loadingCapacity, setLoadingCapacity] = useState(false);

  // Pull existing bookings for every planned date so we can flag "full"
  // weeks honestly before the customer commits.
  useEffect(() => {
    if (!enabled || !selectedDate || !selectedTime) {
      setCapacityByDate({});
      return;
    }
    const dates = computeOccurrenceDates(selectedDate, weeks);
    const dateStrs = dates.map((d) => format(d, "yyyy-MM-dd"));

    setLoadingCapacity(true);
    getPublicSlotUsage(listingId, dateStrs)
      .then((data) => {
        setLoadingCapacity(false);
        const map: Record<string, number> = {};
        for (const row of data || []) {
          if (row.booking_time !== selectedTime) continue;
          map[row.booking_date] = (map[row.booking_date] || 0) +
            (row.number_of_pax || 1);
        }
        setCapacityByDate(map);
      })
      .catch((error) => {
        setLoadingCapacity(false);
        console.error("Capacity fetch failed:", error);
        setCapacityByDate({});
      });
  }, [enabled, selectedDate, selectedTime, weeks, listingId]);

  const planned = useMemo<PlannedOccurrence[]>(() => {
    if (!enabled || !selectedDate || !selectedTime) return [];
    return computeOccurrenceDates(selectedDate, weeks).map((d, i) => {
      const { status, remainingSpots } = evaluateOccurrence(d, {
        schedule,
        capacityForDate: capacityByDate,
        totalSpots,
        pax,
        selectedTime12h: selectedTime,
      });
      return {
        position: i + 1,
        date: d,
        dateISO: format(d, "yyyy-MM-dd"),
        status,
        remainingSpots,
      };
    });
  }, [enabled, selectedDate, selectedTime, weeks, schedule, capacityByDate, totalSpots, pax]);

  const conflicts = useMemo(
    () => planned.filter((p) => p.status !== "available"),
    [planned],
  );

  // The actual occurrences that will be booked once conflicts are resolved.
  const finalOccurrences = useMemo(() => {
    if (conflicts.length === 0) return planned;
    return applyConflictResolution(planned, resolution);
  }, [planned, conflicts, resolution]);

  useEffect(() => {
    onPlanChange(finalOccurrences);
  }, [finalOccurrences, onPlanChange]);

  const totalCost = finalOccurrences.length * pricePerPax * pax;
  const weekday =
    selectedDate !== undefined ? WEEKDAY_NAME[selectedDate.getDay()] : "";

  // Hide entirely until date+time exist. Recurrence is meaningless otherwise.
  const eligible = !!selectedDate && !!selectedTime;
  if (!eligible) return null;

  return (
    <div className="space-y-3 pt-4 border-t">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">
            {billingMode === "upfront" ? "Book the full programme" : "Make recurring"}
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            {billingMode === "upfront"
              ? "Enrol for a block of weekly sessions"
              : "Book the same time every week"}
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={onEnabledChange} />
      </div>

      {enabled && (
        <div className="space-y-4 pt-1">
          <div className="text-sm text-muted-foreground">
            Every <span className="font-medium text-foreground">{weekday}</span>{" "}
            at <span className="font-medium text-foreground">{selectedTime}</span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="recurring-weeks">Number of weeks</Label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setWeeks(Math.max(MIN_WEEKS, weeks - 1))}
                className="h-10 w-10 shrink-0 rounded-md border border-input bg-card flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-50"
                disabled={weeks <= MIN_WEEKS}
              >
                <span className="text-base font-medium">−</span>
              </button>
              <Input
                id="recurring-weeks"
                type="number"
                inputMode="numeric"
                value={weeks}
                min={MIN_WEEKS}
                max={MAX_WEEKS}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (Number.isNaN(v)) {
                    setWeeks(MIN_WEEKS);
                    return;
                  }
                  setWeeks(Math.min(MAX_WEEKS, Math.max(MIN_WEEKS, v)));
                }}
                className="flex-1 text-center font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                type="button"
                onClick={() => setWeeks(Math.min(MAX_WEEKS, weeks + 1))}
                className="h-10 w-10 shrink-0 rounded-md border border-input bg-card flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-50"
                disabled={weeks >= MAX_WEEKS}
              >
                <span className="text-base font-medium">+</span>
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Schedule preview</Label>
            <div className="border border-input rounded-xl divide-y divide-border bg-card">
              {loadingCapacity ? (
                <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Checking availability...
                </div>
              ) : (
                planned.map((p) => (
                  <OccurrenceRow key={p.position} occurrence={p} pax={pax} />
                ))
              )}
            </div>
          </div>

          {conflicts.length > 0 && (
            <div className="space-y-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-destructive" />
                <span>
                  <span className="font-medium text-foreground">
                    {conflicts.length} of {planned.length} weeks {conflicts.length === 1 ? "isn't" : "aren't"} available.
                  </span>{" "}
                  How would you like to proceed?
                </span>
              </div>
              <RadioGroup
                value={resolution}
                onValueChange={(v) => setResolution(v as "skip" | "trim")}
                className="space-y-2 pl-6"
              >
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="skip" id="res-skip" className="mt-0.5" />
                  <Label htmlFor="res-skip" className="font-normal cursor-pointer leading-snug">
                    Skip the unavailable weeks and book the rest
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      You'll book {planned.length - conflicts.length} of the {planned.length} weeks — only the ones shown as available above.
                    </span>
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem value="trim" id="res-trim" className="mt-0.5" />
                  <Label htmlFor="res-trim" className="font-normal cursor-pointer leading-snug">
                    Only book up to the first unavailable week
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Bookings stop at the first week that isn't available — useful if you want consecutive weeks only.
                    </span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {finalOccurrences.length > 0 && finalOccurrences.length < MIN_WEEKS && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-300/40 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <span className="text-foreground">
                Only {finalOccurrences.length} week is bookable as a recurring series — we need at least {MIN_WEEKS}.
                Either pick more weeks, choose a different starting date, or turn off "Make recurring" and book this as a one-off.
              </span>
            </div>
          )}

          <div className="space-y-2 pt-1">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-muted-foreground">
                {finalOccurrences.length} session
                {finalOccurrences.length === 1 ? "" : "s"} × $
                {(pricePerPax * pax).toFixed(2)}
              </span>
              <span className="font-semibold text-foreground">
                ${totalCost.toFixed(2)} total
              </span>
            </div>

            <PaymentTimingExplanation
              paymentMethod={paymentMethod}
              billingMode={billingMode}
              perWeekAmount={pricePerPax * pax}
              totalAmount={totalCost}
              sessionCount={finalOccurrences.length}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Big-total scare prevention. Customers seeing "$3,000 total" on a 6-week
 * series naturally assume we'll charge them the full amount on submit. We
 * don't. Week 1 is the only thing that hits the card (or the venue till)
 * on day one. Weeks 2..N are charged 24h before each slot.
 *
 * The wording adapts to whichever payment method is currently selected so the
 * customer reads a sentence that's exactly true for THEIR booking. If no
 * payment method is picked yet we show the generic shape.
 */
function PaymentTimingExplanation({
  paymentMethod,
  billingMode,
  perWeekAmount,
  totalAmount,
  sessionCount,
}: {
  paymentMethod?: "stripe" | "manual";
  billingMode?: "upfront" | "per_session";
  perWeekAmount: number;
  totalAmount: number;
  sessionCount: number;
}) {
  if (sessionCount < 2 || totalAmount <= 0) return null;
  const perWeek = `$${perWeekAmount.toFixed(2)}`;
  const total = `$${totalAmount.toFixed(2)}`;
  const isUpfront = billingMode === "upfront";

  let title: string;
  let detail: string;

  if (paymentMethod === "stripe" && isUpfront) {
    // Upfront programme: the whole bundle is charged in one go today. No card
    // is saved and there's no weekly drip.
    title = `You'll pay ${total} today for all ${sessionCount} sessions.`;
    detail = `This programme is paid in full when you book — one payment of ${total}, ` +
      `no weekly charges. Refunds for sessions you cancel follow the centre's refund policy.`;
  } else if (paymentMethod === "stripe") {
    title = `You won't be charged ${total} today.`;
    detail = `Only week 1 (${perWeek}) is charged when you book. ` +
      `For each later week, we'll automatically charge your saved card 24 hours before that session. ` +
      `Cancel any week up to 24 hours before its slot for a full refund on that session.`;
  } else if (paymentMethod === "manual") {
    title = `You won't be paying ${total} now.`;
    detail = `Each week you'll pay ${perWeek} at the venue when you show up. ` +
      `Nothing is charged online — the ${total} above is just the total across all ${sessionCount} sessions for your reference.`;
  } else {
    title = `You won't be paying ${total} all at once.`;
    detail = `Recurring bookings are billed per week: ${perWeek} per session. ` +
      `If you pay online, only this week is charged now and each later week 24h before its slot. ` +
      `If you pay at the venue, you pay each week when you arrive.`;
  }

  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/40 border border-border text-xs leading-relaxed">
      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
      <span>
        <span className="font-medium text-foreground">{title}</span>{" "}
        <span className="text-muted-foreground">{detail}</span>
      </span>
    </div>
  );
}

function OccurrenceRow({ occurrence, pax }: { occurrence: PlannedOccurrence; pax: number }) {
  const ok = occurrence.status === "available";
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
        ) : (
          <XCircle className="w-4 h-4 text-destructive shrink-0" />
        )}
        <span className="text-sm font-medium">
          {format(occurrence.date, "EEE dd MMM yyyy")}
        </span>
      </div>
      <span
        className={cn(
          "text-xs text-right",
          ok ? "text-muted-foreground" : "text-destructive",
        )}
      >
        {describeStatus(occurrence.status, {
          remainingSpots: occurrence.remainingSpots,
          pax,
        })}
      </span>
    </div>
  );
}
